import { useState } from "react";
import { ReuseModal } from "../ReuseModal";
import {
  Box,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  Stack,
  Typography,
} from "@mui/material";
import {
  useExportDataMutation,
  useLazyGetPlaybooksCSVQuery,
} from "../../../api";
import { getErrorMessage } from "../../../utils/getErrorMessage";
import { CustomToast } from "../CustomToast";
import { toast } from "react-toastify";
import { INPUT_REGEX } from "../../../utils/regex";
import { openLinkHandler } from "../../../utils/openLinkHandler";
import { SubBlock } from "./SubBlock";
import { ReuseInput } from "../ReuseInput";
import { FetchBaseQueryError } from "@reduxjs/toolkit/query";
import { SerializedError } from "@reduxjs/toolkit";

export const ExportAccountDataModal = ({
  account_id,
  campaign_id,
  isOpen,
  setIsOpen,
  total_members_with_playbook,
  isOnlyPlaybook,
}: {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  account_id: string;
  campaign_id: string;
  total_members_with_playbook: number;
  isOnlyPlaybook: boolean;
}) => {
  const optionList = [
    { label: "All Contacts", hide: isOnlyPlaybook },
    { label: "Probable BC Members", hide: isOnlyPlaybook },
    {
      label: "BDR Engagement Playbook URLs",
      hide: total_members_with_playbook === 0,
    },
  ];

  const [isError, setIsError] = useState<boolean>(false);
  const [fileName, setFileName] = useState<string>("Export-data");
  const [option, setOption] = useState<string>(
    isOnlyPlaybook ? optionList[2].label : optionList[0].label,
  );

  const [exportData, { isLoading }] = useExportDataMutation();

  const [getPlaybooksCSV, { error: playbookError, isFetching }] =
    useLazyGetPlaybooksCSVQuery();

  const errorHandler = (
    error: FetchBaseQueryError | SerializedError | undefined,
  ) => {
    toast.error(
      <CustomToast
        type={"error"}
        text={
          getErrorMessage(error || playbookError).globalError ||
          "Something went wrong. Please try again"
        }
        title={"Export Data Failed"}
      />,
    );
  };

  const submitHandler = async () => {
    const isPlaybooksExport = option === optionList[2].label;
    if (isPlaybooksExport) {
      await getPlaybooksCSV(account_id)
        .unwrap()
        .then((data) => {
          openLinkHandler(data.url, fileName);
        })
        .catch(errorHandler);
    } else {
      await exportData({
        campaign_id,
        file_name: fileName || undefined,
        account_ids: [account_id],
        only_committee: option === optionList[1].label,
      })
        .unwrap()
        .then((data) => {
          openLinkHandler(data.url, fileName);
        })
        .catch(errorHandler);
    }
    setIsOpen(false);
  };

  const inputChangeHandler = (value: string) => {
    setIsError(!INPUT_REGEX.test(value));
    setFileName(value);
  };

  return (
    <ReuseModal
      isOpen={isOpen}
      isLoading={isLoading || isFetching}
      onClose={() => setIsOpen(false)}
      title="Export Data to CSV"
      onSubmit={submitHandler}
      maxWidth="540px"
      submitBtnColor="#1C3C6C"
      submitBtnText="Export"
      isDisabled={isError || isLoading || isFetching}
      isDisabledCancelBtn={isLoading || isFetching}
    >
      <Stack gap="16px">
        <SubBlock
          title=""
          text="Which data set of contacts you would like to export?"
        />
        <FormControl sx={{ padding: "0 12px", alignItems: "flex-start" }}>
          <RadioGroup
            aria-labelledby="demo-radio-buttons-group-label"
            defaultValue={option}
            value={option}
            name="radio-buttons-group"
            onChange={(e, el) => setOption(el)}
          >
            {optionList.map(({ label, hide }) => {
              if (hide) return null;
              return (
                <FormControlLabel
                  key={label}
                  value={label}
                  control={<Radio />}
                  label={label}
                />
              );
            })}
          </RadioGroup>
        </FormControl>
        <SubBlock title="File name" text="What should be the CSV file name?" />
        <Stack direction="row" gap="8px" alignItems="center" pt="16px">
          <Box
            sx={{
              position: "relative",
              width: "100%",
            }}
          >
            <ReuseInput
              name="company_name"
              handleInputChange={(key, value) => inputChangeHandler(value)}
              errorMessage={
                "Must contain only letters, numbers, and basic punctuation"
              }
              value={fileName}
              label="File Name*"
              placeholder=" e.g., INFUSE"
              isError={isError}
              setErrorHandler={(key, value) => setIsError(value)}
            />
          </Box>

          <Typography
            component="span"
            variant="body1"
            sx={{ fontWeight: 500, lineHeight: "26px" }}
            color="#4C545B"
          >
            .csv
          </Typography>
        </Stack>
      </Stack>
    </ReuseModal>
  );
};
